import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Space, Row, Col, Result, message } from "antd";
import {
  Document,
  Font,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import { useStepsForm } from "sunflower-antd";
import dayjs from "dayjs";

interface IModalProps {
  show: boolean;
  invoice: any;
  showInvoiceModal: Function;
}

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "/fonts/Roboto-Regular.ttf",
      fontWeight: 400,
    },
    {
      src: "/fonts/Roboto-Bold.ttf",
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#333",
  },
  text: {
    fontFamily: "Roboto",
    fontSize: "10px",
    marginBottom: "2px",
  },
  textBold: {
    fontFamily: "Roboto",
    fontSize: "10px",
    fontWeight: "bold",
  },
  headingSection: {
    marginBottom: 50,
  },
  section: {
    marginBottom: 10,
  },
  heading: {
    fontSize: 18,
    fontWeight: "bold",
    fontFamily: "Roboto",
  },
  label: {
    fontWeight: "bold",
  },
  value: {
    marginLeft: 5,
  },
  logo: {
    width: "25%",
    textAlign: "center",
  },
  container: {
    border: "1px solid black",
    width: "100%",
    marginBottom: "16px",
    padding: "6px",
  },
  row: {
    flexDirection: "row",
  },
  column: {
    flex: 1,
    padding: "6px",
  },
  table: {
    width: "100%",
    textAlign: "left",
  },
});

const FuneralInvoiceModal: React.FC<IModalProps> = ({
  show,
  invoice,
  showInvoiceModal,
}) => {
  const [date, setDate] = useState("");

  const handleModal = () => {
    showInvoiceModal(invoice);
    gotoStep(0);
    form.resetFields();
  };

  const handleDownload = () => {
    setTimeout(() => {
      showInvoiceModal(invoice);
      gotoStep(0);
      message.success("Document has started downloading.");
    }, 100);
  };

  useEffect(() => {
    const localeDate = new Date();
    const monthIndex = localeDate.getMonth();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthName = monthNames[monthIndex];
    const year = localeDate.getFullYear();

    setDate(`${monthName} ${year}`);
  }, []);

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      console.log("Printing Statement");
    },
    total: 2,
  });

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 8 },
  };

  const tailLayout = {
    labelCol: { span: 0 },
    wrapperCol: { offset: 8, span: 8 },
  };

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={{ padding: "50px" }}>
        <View>
          <View
            style={[
              styles.headingSection,
              {
                width: "100%",
                flexDirection: "row",
              },
            ]}
          >
            <View style={[styles.section, { width: "50%" }]}>
              <Text style={[styles.heading, { textAlign: "left" }]}>
                {invoice.Group.GroupName}
              </Text>
              <Text style={styles.text}>{invoice.Group.AddressLine1}</Text>
              <Text style={styles.text}>{invoice.Group.AddressLine2}</Text>
              <Text style={styles.text}>{invoice.Group.Suburb}</Text>
              <Text style={styles.text}>{invoice.Group.Province}</Text>
              <Text style={styles.text}>{invoice.Group.Cellphone}</Text>
            </View>

            <View
              style={[styles.section, { width: "50%", textAlign: "right" }]}
            >
              <Text
                style={[
                  styles.heading,
                  { fontSize: "16px", marginBottom: "2px", textAlign: "right" },
                ]}
              >
                Invoice
              </Text>
              <Text
                style={[
                  styles.heading,
                  { fontSize: "16px", marginBottom: "2px", textAlign: "right" },
                ]}
              >
                {invoice.OutstandingBalance > 0 ? "OUTSTANDING" : "PAID"}
              </Text>
              <Image
                style={styles.logo}
                src={{
                  uri: invoice.Group.CompanyLogo,
                  method: "GET",
                  headers: {},
                  body: "",
                }}
                cache={false}
              />
            </View>
          </View>
          <View
            style={{
              marginBottom: "16px",
              width: "100%",
              flexDirection: "row",
            }}
          >
            <View style={[styles.section, { width: "50%" }]}>
              <Text
                style={[
                  styles.textBold,
                  {
                    fontSize: "10px",
                    borderBottom: "1px solid black",
                    marginBottom: "2px",
                  },
                ]}
              >
                BILL TO
              </Text>
              <Text style={styles.text}>
                {invoice.FuneralCustomer.Name} {invoice.FuneralCustomer.Surname}
              </Text>
              <Text style={styles.text}>
                {invoice.FuneralCustomer.AddressLine1},{" "}
                {invoice.FuneralCustomer.AddressLine2}
              </Text>
              <Text style={styles.text}>
                {invoice.FuneralCustomer.Cellphone}
              </Text>
            </View>
            <View style={{ width: "20%" }}></View>
            <View style={[styles.section, { width: "15%", textAlign: "left" }]}>
              <Text style={[styles.textBold, { fontSize: "9px" }]}>
                Invoice No:
              </Text>
              <Text style={[styles.textBold, { fontSize: "9px" }]}>
                Invoice Date:
              </Text>
              <Text style={[styles.textBold, { fontSize: "9px" }]}>
                Due Date:
              </Text>
            </View>
            <View style={[styles.section, { width: "15%", textAlign: "left" }]}>
              <Text style={[styles.text, { fontSize: "8px" }]}>
                {invoice.InvoiceNumber}
              </Text>
              <Text style={[styles.text, { fontSize: "8px" }]}>
                {dayjs(invoice.Date).format("YYYY-MM-DD")}
              </Text>
              <Text style={[styles.text, { fontSize: "8px" }]}>N/A</Text>
            </View>
          </View>

          <View
            style={{
              marginBottom: "16px",
              width: "100%",
              flexDirection: "row",
              fontSize: "10px",
            }}
          >
            <View style={{ width: "60%" }}>
              <Text
                style={[
                  styles.textBold,
                  {
                    border: "1px solid black",
                    padding: "8px",
                    fontSize: "10px",
                    textAlign: "center",
                  },
                ]}
              >
                DESCRIPTION
              </Text>
              {invoice?.FuneralInvoiceLineItem.map(
                (invoice: any, index: any) => (
                  <Text
                    key={index}
                    style={[
                      {
                        fontFamily: "Roboto",
                        border: "1px solid black",
                        padding: "8px",
                        fontSize: "10px",
                      },
                    ]}
                  >
                    {invoice.FuneralProduct.Description}
                  </Text>
                )
              )}
            </View>
            <View style={{ width: "10%" }}>
              <Text
                style={[
                  styles.textBold,
                  {
                    border: "1px solid black",
                    padding: "8px",
                    fontSize: "10px",
                    textAlign: "center",
                  },
                ]}
              >
                QTY
              </Text>
              {invoice?.FuneralInvoiceLineItem.map(
                (invoice: any, index: any) => (
                  <Text
                    key={index}
                    style={[
                      {
                        fontFamily: "Roboto",
                        border: "1px solid black",
                        padding: "8px",
                        fontSize: "10px",
                      },
                    ]}
                  >
                    {invoice.Quantity}
                  </Text>
                )
              )}
            </View>
            <View style={{ width: "20%" }}>
              <Text
                style={[
                  styles.textBold,
                  {
                    border: "1px solid black",
                    padding: "8px",
                    fontSize: "10px",
                    textAlign: "center",
                  },
                ]}
              >
                PRICE
              </Text>
              {invoice?.FuneralInvoiceLineItem.map(
                (invoice: any, index: any) => (
                  <Text
                    key={index}
                    style={[
                      {
                        fontFamily: "Roboto",
                        border: "1px solid black",
                        padding: "8px",
                        fontSize: "10px",
                      },
                    ]}
                  >
                    R {invoice.FuneralProduct.Price}
                  </Text>
                )
              )}
            </View>
            <View style={{ width: "10%" }}>
              <Text
                style={[
                  styles.textBold,
                  {
                    border: "1px solid black",
                    padding: "8px",
                    fontSize: "10px",
                    textAlign: "center",
                  },
                ]}
              >
                TOTAL
              </Text>
              {invoice?.FuneralInvoiceLineItem.map(
                (invoice: any, index: any) => (
                  <Text
                    key={index}
                    style={[
                      {
                        fontFamily: "Roboto",
                        border: "1px solid black",
                        padding: "8px",
                        fontSize: "10px",
                      },
                    ]}
                  >
                    R {invoice.FuneralProduct.Price * invoice.Quantity}
                  </Text>
                )
              )}
            </View>
          </View>

          <View
            style={{
              marginBottom: "16px",
              width: "100%",
              flexDirection: "row",
              fontSize: "10px",
            }}
          >
            <View style={{ width: "60%" }}>
              <Text
                style={[
                  styles.textBold,
                  {
                    padding: "16px",
                    fontSize: "10px",
                    textAlign: "center",
                  },
                ]}
              >
                Thank you for you business.
              </Text>
            </View>
            <View style={{ width: "10%" }}></View>
            <View style={{ width: "20%" }}>
              <Text
                style={[
                  styles.text,
                  {
                    padding: "8px",
                    fontSize: "8px",
                    textAlign: "right",
                  },
                ]}
              >
                SUBTOTAL
              </Text>
              <Text
                style={[
                  styles.text,
                  {
                    padding: "8px",
                    fontSize: "8px",
                    textAlign: "right",
                  },
                ]}
              >
                TAXRATE
              </Text>
              <Text
                style={[
                  styles.text,
                  {
                    padding: "8px",
                    fontSize: "8px",
                    textAlign: "right",
                    borderBottom: "1px solid black",
                  },
                ]}
              >
                TOTAL TAX
              </Text>
              <Text
                style={[
                  styles.textBold,
                  {
                    padding: "8px",
                    fontSize: "10px",
                    textAlign: "right",
                    fontWeight: "bold",
                  },
                ]}
              >
                BALANCE DUE
              </Text>
            </View>
            <View style={{ width: "10%" }}>
              <Text
                style={[
                  styles.text,
                  {
                    padding: "8px",
                    fontSize: "8px",
                    textAlign: "right",
                  },
                ]}
              >
                R {invoice.GrossTotal}
              </Text>
              <Text
                style={[
                  styles.text,
                  {
                    padding: "8px",
                    fontSize: "8px",
                    textAlign: "right",
                  },
                ]}
              >
                {invoice.Group.VATEnabled ? "15%" : "0"}
              </Text>
              <Text
                style={[
                  styles.text,
                  {
                    padding: "8px",
                    fontSize: "8px",
                    textAlign: "right",
                    borderBottom: "1px solid black",
                  },
                ]}
              >
                R {invoice.VATTotal}
              </Text>
              <Text
                style={[
                  styles.textBold,
                  {
                    padding: "8px",
                    fontSize: "10px",
                    textAlign: "right",
                    fontWeight: "bold",
                  },
                ]}
              >
                R {invoice.NettTotal}
              </Text>
            </View>
          </View>
          <View>
            <Text
              style={[
                styles.textBold,
                {
                  borderBottom: "1px solid black",
                  width: "40%",
                  textAlign: "left",
                  marginBottom: "4px",
                },
              ]}
            >
              Terms & Conditions
            </Text>
            <Text style={styles.text}>
              Please pay within specified time to {invoice.Group.GroupName}.
            </Text>
            <Text
              style={[
                styles.textBold,
                {
                  borderBottom: "1px solid black",
                  width: "40%",
                  textAlign: "left",
                  marginBottom: "4px",
                },
              ]}
            >
              Banking Details
            </Text>
            <Text style={styles.text}>{invoice.Group.Bank}</Text>
            <Text style={styles.text}>{invoice.Group.BranchCode}</Text>
            <Text style={styles.text}>{invoice.Group.AccountNumber}</Text>
            <Text style={styles.text}>{invoice.Group.AccountType}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );

  const formList: any = [
    <>
      <div style={{ margin: "0 25px 25px 25px" }}>
        <Row style={{ marginBottom: "30px" }}>
          <Col span={12}>
            <h2 style={{ textAlign: "left" }}>{invoice.Group.GroupName}</h2>
            <p>{invoice.Group.AddressLine1}</p>
            <p>{invoice.Group.AddressLine2}</p>
            <p>{invoice.Group.Suburb}</p>
            <p>{invoice.Group.Province}</p>
            <p>{invoice.Group.Cellphone}</p>
          </Col>
          <Col span={12}>
            <h2
              style={{
                marginBottom: "2px",
                textAlign: "right",
              }}
            >
              Invoice
            </h2>
            <h3
              style={{
                marginBottom: "2px",
                textAlign: "right",
              }}
            >
              {invoice.OutstandingBalance > 0 ? "OUTSTANDING" : "PAID"}
            </h3>
            <img src={invoice.Group.CompanyLogo} style={{ width: "25%" }} />
          </Col>
        </Row>
        <Row style={{ marginBottom: "16px", width: "100%", display: "flex" }}>
          <Col span={6}>
            <p
              style={{
                fontSize: "14px",
                borderBottom: "1px solid black",
                marginBottom: "2px",
                fontWeight: "bold",
              }}
            >
              BILL TO
            </p>
            <p>
              {invoice.FuneralCustomer.Name} {invoice.FuneralCustomer.Surname}
            </p>
            <p>
              {invoice.FuneralCustomer.AddressLine1},{" "}
              {invoice.FuneralCustomer.AddressLine2}
            </p>
            <p>{invoice.FuneralCustomer.Cellphone}</p>
          </Col>
          <Col span={12}></Col>
          <Col span={3} style={{ textAlign: "right" }}>
            <p style={{ fontSize: "12px", fontWeight: "bold" }}>Invoice No:</p>
            <p style={{ fontSize: "12px", fontWeight: "bold" }}>
              Invoice Date:
            </p>
            <p style={{ fontSize: "12px", fontWeight: "bold" }}>Due Date:</p>
          </Col>
          <Col span={3} style={{ textAlign: "right" }}>
            <p style={{ fontSize: "12px" }}>{invoice.InvoiceNumber}</p>
            <p style={{ fontSize: "12px" }}>
              {dayjs(invoice.Date).format("YYYY-MM-DD")}
            </p>
            <p style={{ fontSize: "12px" }}>N/A</p>
          </Col>
        </Row>

        <Row style={{ marginBottom: "16px" }}>
          <table
            style={{
              width: "100%",
              textAlign: "left",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "center",
                    border: "1px solid black",
                    padding: "8px",
                  }}
                >
                  DESCRIPTION
                </th>
                <th style={{ textAlign: "center", border: "1px solid black" }}>
                  QTY
                </th>
                <th style={{ textAlign: "center", border: "1px solid black" }}>
                  PRICE
                </th>
                <th style={{ textAlign: "center", border: "1px solid black" }}>
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.FuneralInvoiceLineItem.map(
                (item: any, index: number) => {
                  return (
                    <tr key={item.id}>
                      <td style={{ border: "1px solid black", padding: "8px" }}>
                        {item.FuneralProduct.Description}
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          border: "1px solid black",
                        }}
                      >
                        {item.Quantity}
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          border: "1px solid black",
                        }}
                      >
                        R {item.FuneralProduct.Price}
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          border: "1px solid black",
                        }}
                      >
                        R{" "}
                        {Number(item.Quantity) *
                          Number(item.FuneralProduct.Price)}
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </Row>

        <div
          style={{
            marginBottom: "16px",
            width: "100%",
            display: "flex",
            fontSize: "10px",
          }}
        >
          <div style={{ width: "60%" }}></div>
          <div style={{ width: "10%" }}></div>
          <div style={{ width: "20%" }}>
            <p style={{ padding: "8px", fontSize: "14px", textAlign: "right" }}>
              SUBTOTAL
            </p>
            <p style={{ padding: "8px", fontSize: "14px", textAlign: "right" }}>
              TAXRATE
            </p>
            <p
              style={{
                padding: "8px",
                fontSize: "14px",
                textAlign: "right",
                borderBottom: "1px solid black",
              }}
            >
              TOTAL TAX
            </p>
            <p
              style={{
                padding: "8px",
                fontSize: "14px",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              BALANCE DUE
            </p>
          </div>
          <div style={{ width: "10%" }}>
            <p style={{ padding: "8px", fontSize: "14px", textAlign: "right" }}>
              R {invoice.GrossTotal}
            </p>
            <p style={{ padding: "8px", fontSize: "14px", textAlign: "right" }}>
              {invoice.Group.VATEnabled ? "15%" : "0"}
            </p>
            <p
              style={{
                padding: "8px",
                fontSize: "14px",
                textAlign: "right",
                borderBottom: "1px solid black",
              }}
            >
              R {invoice.VATTotal}
            </p>
            <p
              style={{
                padding: "8px",
                fontSize: "14px",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              R {invoice.NettTotal}
            </p>
          </div>
        </div>
        <div>
          <p
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              borderBottom: "1px solid black",
              width: "40%",
              textAlign: "left",
              marginBottom: "4px",
            }}
          >
            Terms & Conditions
          </p>
          <p>Please pay within specified time to {invoice.Group.GroupName}.</p>
          <p
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              borderBottom: "1px solid black",
              width: "40%",
              textAlign: "left",
              marginBottom: "4px",
            }}
          >
            Banking Details
          </p>
          <p>{invoice.Group.Bank}</p>
          <p>{invoice.Group.BranchCode}</p>
          <p>{invoice.Group.AccountNumber}</p>
          <p>{invoice.Group.AccountType}</p>
        </div>
      </div>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <PDFDownloadLink
            document={<MyDocument />}
            fileName={
              invoice.FuneralCustomer.Name +
              " " +
              invoice.FuneralCustomer.Surname +
              "_Invoice"
            }
            style={{ marginBottom: 10 }}
          >
            <Button block size="large" onClick={handleDownload}>
              Download Statement
            </Button>
          </PDFDownloadLink>
        </Space>
      </Form.Item>
    </>,
  ];

  return (
    <Modal
      title="Invoice"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={null}
    >
      {/* Steps Form */}
      <div>
        <div style={{ marginTop: 60 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 900 }}>
            {formList[current]}
          </Form>

          {current === 1 && (
            <Result status="success" title="Successfully submitted.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 2 && (
            <Result status="error" title="Error occurred, please try again.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FuneralInvoiceModal;
